import Head from "next/head";
import Link from "next/link";
import React from "react";
import { NextPage } from "next";

import ChartLineIcon from "src/components/icons/ChartLineIcon";
import ChartTimelineIcon from "src/components/icons/ChartTimelineIcon";
import CoreLogo from "src/components/logos/CoreLogo";
import CubeIcon from "src/components/icons/CubeIcon";
import MapMarkerIcon from "src/components/icons/MapMarkerIcon";
import SearchIcon from "src/components/icons/SearchIcon";
import { useUser } from "src/hooks/useUser";
import SignupCallout from "src/components/sales/SignupCallout";
import PlatformExploreBanner from "src/components/sales/PlaformExploreBanner";
import { socialMetadata } from "src/components/layout/Head";
import GitHubIcon from "../../components/icons/GitHubIcon";

type CoreProps = {
  signup: (mailchimpUser?: any) => void;
};

const Core: NextPage<CoreProps> = ({ signup }) => {
  return (
    <>
      <Head>
        {socialMetadata({
          title: "HASH Core",
          description: "Integrated simulation development environment",
        })}
      </Head>
      <div className="platform-page core">
        <div className="mainsite-header with-breadcrumb with-image">
          <div className="container">
            <div className="header-image">
              <CoreLogo />
            </div>
            <div className="header-text">
              <div className="mainsite-breadcrumb">
                <Link href="/platform">
                  <a>
                    Platform <span>{">"}</span>
                  </a>
                </Link>
              </div>
              <div className="title-with-button">
                <div className="mainsite-title">
                  <h1 className="heading-1">
                    <span className="bright-blue">h</span>Core
                  </h1>
                  <p className="large-text">
                    Integrated data-development environment
                  </p>
                </div>
                <a href="https://github.com/hashintel/labs" target="_blank">
                  <button className="primary-button engine">
                    Star on<span>&nbsp;GitHub</span> <GitHubIcon />
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="platform-content">
          <div className="container">
            <p className="container-title">
              Build and run simulations with millions of agents from your laptop
            </p>

            <h3 className="heading-4">WHY BUILD IN A BROWSER?</h3>

            <div className="paras">
              <div className="para">
                <h2 className="para-title">Ready-to-go environment</h2>
                <p>
                  <strong>HASH Core</strong> is a zero-setup environment for
                  in-browser simulation development, viewing and
                  experimentation. No packages to install, no config required.
                </p>
                <p>
                  It integrates natively with{" "}
                  <Link href="/platform/index">
                    <a>HASH</a>
                  </Link>{" "}
                  for package and data management, as well as HASH's
                  distributed{" "}
                  <Link href="/platform/cloud">
                    <a>cloud compute service</a>
                  </Link>{" "}
                  to provide everything you need to run simulations and
                  experiments at scale.
                </p>
              </div>

              <div className="para">
                <h2 className="para-title">Open and reproducible</h2>
                <p>
                  We believe reproducible results are key to accountable
                  decision-making, and in turn to good governance. All this
                  requires a trusted execution platform.
                </p>
                <p>
                  Every simulation run in <strong>Core</strong> is both
                  reproducible and portable, with in-browser and cloud
                  simulation alike powered by our open, fair-source{" "}
                  <Link href="/platform/engine">
                    <a>HASH Engine</a>
                  </Link>
                  .
                </p>
              </div>

              <div className="para">
                <h2 className="para-title">Easy and simple</h2>
                <p>
                  Most simulations represent the world either spatially or as a
                  network. <strong>Core</strong> natively supports both
                  approaches, through a variety of built-in viewers:
                </p>
                <ul>
                  <li>
                    <span className="icon-wrapper">
                      <CubeIcon />
                    </span>
                    3D
                  </li>
                  <li>
                    <span className="icon-wrapper">
                      <MapMarkerIcon />
                    </span>
                    Geospatial
                  </li>
                  <li>
                    <span className="icon-wrapper">
                      <SearchIcon />
                    </span>
                    Single-Step Explorer
                  </li>
                  <li>
                    <span className="icon-wrapper">
                      <ChartLineIcon />
                    </span>
                    Whole-Run Analysis
                  </li>
                  <li>
                    <span className="icon-wrapper">
                      <ChartTimelineIcon />
                    </span>
                    Multi-Run Analysis
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="form-wrapper">
            <SignupCallout
              Icon={<CoreLogo />}
              purple
              signup={signup}
              subtitle="Create a free account to get started building your own models"
              title="Sign up for free to use HASH Core"
            />
          </div>

          <PlatformExploreBanner exclude="Core" />
        </div>
      </div>
    </>
  );
};

export default Core;
